/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { usePagination, useSortBy, useTable } from 'react-table';
import Skeleton from 'react-loading-skeleton';
import styles from './TeamsTable.module.less';
import sortingUp from '../../../../../static/img/blue-asc-sort.svg';
import sortingDown from '../../../../../static/img/blue-desc-sort.svg';
import sorting from '../../../../../static/img/sorting.png';
import { CellTypes } from '../../Users/constants';
import ActionCell from './ActionCell';
import TeamCell from './TeamCell';
import UsersCell from './UsersCell';
import OpenSelect from '../../../Components/OpenSelect/OpenSelect';

function TeamsTable({ columns, data, tableProps, isLoading }) {
  const defaultColumn = {
    ActionCell,
    UsersCell,
    TeamCell,
  };
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    page,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useSortBy,
    usePagination,
  );

  const getColumnStyle = (column) => {
    let style = '';

    switch (column) {
      case 0:
        style = styles.teamColumn;
        break;
      case 1:
        style = styles.usersColumn;
        break;
      default:
        style = '';
    }

    return style;
  };

  const getRowStyle = (header, cell, hover, row) => {
    if (hover.id === row.id) {
      return styles.hoverRow;
    }
    return null;
  };

  const getCellType = (cell, hover) => {
    let type = '';

    switch (cell.column.id) {
      case 0:
        type = CellTypes.TEAM_CELL;
        break;
      case 1:
        if (cell.row.id === hover.id) {
          type = CellTypes.ACTION_CELL;
          break;
        } else {
          type = CellTypes.USERS_CELL;
          break;
        }
      default:
        type = CellTypes.TEAM_CELL;
    }
    return type;
  };
  const pageNumbers = () => {
    const TOTAL_SHOW = 5;
    const currentPage = pageIndex + 1;
    const middle = parseInt(TOTAL_SHOW / 2, 10);
    const startIndex =
      pageCount > TOTAL_SHOW
        ? Math.max(1, currentPage - middle) -
          (pageCount - currentPage < middle ? middle - (pageCount - currentPage) : 0)
        : 1;
    const endIndex = Math.min(pageCount, startIndex + TOTAL_SHOW);

    const numbers = [];
    for (let i = startIndex; i <= endIndex; i++) {
      numbers.push(i);
    }

    const startRange = startIndex - 1;

    return (
      <>
        {startRange >= middle - 1 && (
          <span
            tabIndex={0}
            role="button"
            onClick={() => gotoPage(startRange - 1)}
            className={startRange === currentPage ? styles.selectedPageNumber : styles.additionalPagesNumber}
            key={startRange}
          >
            ...
          </span>
        )}

        {numbers.map((pageNumber) => (
          <span
            tabIndex={0}
            role="button"
            onClick={() => gotoPage(pageNumber - 1)}
            className={pageNumber === currentPage ? styles.selectedPageNumber : styles.additionalPagesNumber}
            key={pageNumber}
          >
            {pageCount > TOTAL_SHOW &&
            pageNumber === endIndex &&
            (pageCount - middle > currentPage || pageNumber !== pageCount)
              ? '...'
              : pageNumber}
          </span>
        ))}
      </>
    );
  };

  function getButtonStyle(canPrevNext) {
    const buttonStyle = [styles.paginationButton];

    if (!canPrevNext) {
      buttonStyle.push(styles.disabledButton);
    }

    return buttonStyle.join(' ');
  }
  const options = [
    { label: '10 Per Page', value: 10 },
    { label: '50 Per Page', value: 50 },
    { label: '100 Per Page', value: 100 },
  ];

  const [isHover, setHover] = useState({ id: null });
  return (
    <div className={styles.tableContainer}>
      <table className={styles.usersTable} {...getTableProps()} id="teams-table">
        <thead>
          {headerGroups.map((group) => (
            <tr {...group.getHeaderGroupProps()}>
              {group.headers.map((column) => {
                return (
                  <th className={getColumnStyle(column.id)} {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <img src={sortingUp} alt="" />
                        ) : (
                          <img src={sortingDown} alt="" />
                        )
                      ) : (
                        <img src={sorting} alt="" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        {!isLoading && data.length > 0 ? (
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);

              return (
                <tr
                  {...row.getRowProps()}
                  id={`teams-row-${i}`}
                  onMouseEnter={() => setHover({ id: row.id })}
                  onMouseLeave={() => setHover({ id: null })}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td className={getRowStyle(cell.column.Header, cell, isHover, row)} {...cell.getCellProps()}>
                        {cell.render(getCellType(cell, isHover), {
                          tableProps,
                        })}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : !isLoading && data.length === 0 ? (
          <tbody {...getTableBodyProps()}>
            <tr>
              <td colSpan={2}>
                <label className={styles.emptyTableText}>{gettext('No teams created')}</label>
              </td>
            </tr>
          </tbody>
        ) : null}
        <tfoot />
      </table>
      {isLoading && (
        <div style={{ fontSize: 20, lineHeight: 2, width: '100%', marginTop: 20 }}>
          <Skeleton count={10} />
        </div>
      )}
      {data.length > 0 ? (
        <div className={styles.tableFooter}>
          <div className={styles.paginationContainer}>
            <button
              type="button"
              className={getButtonStyle(canPreviousPage)}
              onClick={previousPage}
              disabled={!canPreviousPage}
            >
              {'<< '}
              {gettext('Previous')}
            </button>
            <div className={styles.pageNumbersContainer}>{pageNumbers()}</div>
            <button type="button" className={getButtonStyle(canNextPage)} onClick={nextPage} disabled={!canNextPage}>
              {gettext('Next')}
              {' >>'}
            </button>
          </div>
          <div className={styles.paginationContainer}>
            <div style={{ width: 200 }}>
              <OpenSelect
                id="select-page-size"
                name="select-page-size"
                options={options}
                value={options.find((o) => o.value === pageSize)}
                setValue={(op) => setPageSize(op)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

TeamsTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  tableProps: PropTypes.object,
  isLoading: PropTypes.bool,
};

export default TeamsTable;
